import type excel from "exceljs";
import { BLACK_BORDER } from "./constants";
import type { MergeRequest, RowData, SavedMerge, SheetWithMerges } from "./types";

// ─── Cell helpers (shared with importPart.ts / importPartV1.ts) ───────────────

export const getCellStr = (
  sheet: excel.Worksheet,
  row: number,
  col: number,
): string => sheet.getCell(row, col).value?.toString().trim() ?? "";

export const getCellNum = (
  sheet: excel.Worksheet,
  row: number,
  col: number,
): number => {
  const val = sheet.getCell(row, col).value;
  if (val === null || val === undefined || val === "") return 0;
  return parseFloat(val.toString().replace(/,/g, "")) || 0;
};

export const fillMaterialTypeSpecSheet = (
  sheet: excel.Worksheet,
  rows: { material_type_name: string; spec_name: string }[],
) => {
  rows.forEach(({ material_type_name, spec_name }, index) => {
    const rowNumber = index + 2;
    const typeCell = sheet.getCell(rowNumber, 1);
    const specCell = sheet.getCell(rowNumber, 2);
    typeCell.value = material_type_name;
    specCell.value = spec_name;
    typeCell.border = BLACK_BORDER;
    specCell.border = BLACK_BORDER;
  });
};

export const fillDimensionSheet = (
  sheet: excel.Worksheet,
  rows: { id: number; dimension_name: string }[],
) => {
  rows.forEach(({ dimension_name }, index) => {
    const rowNumber = index + 2;
    const cell = sheet.getCell(rowNumber, 1);
    cell.value = dimension_name;
    cell.border = BLACK_BORDER;
  });
};

export const triggerDownload = (buffer: excel.Buffer, fileName: string) => {
  const blob = new Blob([buffer as ArrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const fillHeaderInfo = (
  sheet: excel.Worksheet,
  labels: Record<string, string>,
) => {
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber > 11) return;
    row.eachCell((cell, colNumber) => {
      const label = cell.value?.toString().trim() ?? "";
      if (label in labels) {
        const valueCell = sheet.getCell(rowNumber, colNumber + 1);
        valueCell.value = labels[label];
        valueCell.border = BLACK_BORDER;
      }
    });
  });
};

export const findInsertRow = (
  sheet: excel.Worksheet,
  keyword: string,
  skip: number,
): number => {
  let insertRowIndex = -1;
  sheet.eachRow((row, rowNumber) => {
    row.eachCell((cell) => {
      if (cell.value?.toString().trim() === keyword) {
        insertRowIndex = rowNumber + skip;
      }
    });
  });
  return insertRowIndex;
};

const getAllMerges = (sheet: excel.Worksheet): SavedMerge[] => {
  const sheetWithMerges = sheet as SheetWithMerges;
  const merges: SavedMerge[] = [];
  if (sheetWithMerges._merges) {
    for (const key in sheetWithMerges._merges) {
      const merge = sheetWithMerges._merges[key];
      if (merge?.model) {
        merges.push({
          top: merge.model.top,
          left: merge.model.left,
          bottom: merge.model.bottom,
          right: merge.model.right,
        });
      }
    }
  }
  return merges;
};

const clearAllMerges = (sheet: excel.Worksheet) => {
  const sheetWithMerges = sheet as SheetWithMerges;
  if (sheetWithMerges._merges) {
    for (const key of Object.keys(sheetWithMerges._merges)) {
      delete sheetWithMerges._merges[key];
    }
  }
};

export const spliceRowsPreservingMerges = (
  sheet: excel.Worksheet,
  insertRow: number,
  rows: RowData[],
) => {
  const savedMerges = getAllMerges(sheet);
  clearAllMerges(sheet);

  sheet.spliceRows(insertRow, 0, ...rows);

  const insertedCount = rows.length;
  for (const merge of savedMerges) {
    let newTop = merge.top;
    let newBottom = merge.bottom;

    if (merge.top >= insertRow) {
      newTop += insertedCount;
      newBottom += insertedCount;
    } else if (merge.bottom >= insertRow) {
      newBottom += insertedCount;
    }

    if (newTop === newBottom && merge.left === merge.right) continue;

    try {
      sheet.mergeCells(newTop, merge.left, newBottom, merge.right);
    } catch {
      // Merge conflict — ignore
    }
  }
};

export const applyMergesAndBorders = (
  sheet: excel.Worksheet,
  mergeRequests: MergeRequest[],
  insertRowIndex: number,
  rowCount: number,
  rowToBorder: number[],
  centerCols: number[] = [],
  rightCols: number[] = [],
  rowHeight: number = 14.4,
) => {
  const borderColumns = Array.isArray(rowToBorder) ? rowToBorder : [];

  const sheetWithMerges = sheet as SheetWithMerges;
  const sheetMerges = sheetWithMerges._merges;
  const endRowLimit = insertRowIndex + rowCount - 1;

  if (sheetMerges) {
    const keysToUnmerge: string[] = [];

    for (const key in sheetMerges) {
      const merge = sheetMerges[key];
      if (!merge || !merge.model) continue;

      const { top, bottom } = merge.model;

      if (top >= insertRowIndex && bottom <= endRowLimit) {
        keysToUnmerge.push(key);
      }
    }

    for (const key of keysToUnmerge) {
      delete sheetMerges[key];
    }
  }

  for (const { startRow, endRow, col } of mergeRequests) {
    try {
      sheet.mergeCells(startRow, col, endRow, col);
      sheet.getCell(startRow, col).alignment = {
        vertical: "top",
        wrapText: true,
      };
    } catch (e) {
      console.warn(
        `ข้ามการ Merge ที่ Col ${col}, แถว ${startRow}-${endRow} เพราะติดเลย์เอาต์เดิม:`,
        e,
      );
    }
  }

  for (let r = insertRowIndex; r < insertRowIndex + rowCount; r++) {
    sheet.getRow(r).height = rowHeight;
    for (const col of borderColumns) {
      const cell = sheet.getCell(r, col);
      cell.border = BLACK_BORDER;
      cell.alignment = { ...cell.alignment, vertical: "top" };
    }
    for (const col of centerCols) {
      sheet.getCell(r, col).alignment = {
        horizontal: "center",
        vertical: "top",
      };
    }
    for (const col of rightCols) {
      sheet.getCell(r, col).alignment = {
        horizontal: "right",
        vertical: "top",
      };
    }
  }
};
